"use client";

import { fetchWorkflowById } from "@/app/(v2)/workflows/_hooks/workflowsApi";
import { submitPendingPrompt } from "@/lib/workflow/submitPendingPrompt";
import { useAIChatStore } from "@/stores/aiChatStore";
import { useWorkflowsStore, type PublishWidgetRequest, type WorkflowVisibility } from "@/stores/workflowsStore";
import { defineVoiceTool } from "realtime-voice-component";
import { z } from "zod";
import {
  B3OS_VOICE_EDITOR_COMMAND_EVENT,
  B3OS_VOICE_SELECT_WORKFLOW_NODE_EVENT,
  B3OS_VOICE_UPDATE_WORKFLOW_NODE_EVENT,
  type VoiceRiskLevel,
} from "./voiceCapabilities";
import { compactNode, compactWorkflow } from "./voiceCompact";
import { getCurrentWorkflowId, resolveWorkflow, resolveWorkflowNode } from "./voiceEntityResolvers";
import { ensurePermission, requireConfirmation } from "./voiceToolRuntime";
import { type B3OSVoiceToolsOptions, workflowVisibilitySchema } from "./voiceToolTypes";

const workflowEditorCommandSchema = z.enum(["save_draft", "run_test", "publish"]);

function dispatchVoiceEditorCommand(action: z.infer<typeof workflowEditorCommandSchema>) {
  window.dispatchEvent(
    new CustomEvent(B3OS_VOICE_EDITOR_COMMAND_EVENT, {
      detail: { action },
    }),
  );
}

export function createWorkflowActionTool(options: B3OSVoiceToolsOptions) {
  return defineVoiceTool({
    name: "workflow_action",
    description:
      "Perform a workflow operation after resolving a workflow by id, name, or the currently open workflow. Write/destructive/financial operations require confirmation.",
    parameters: z.object({
      action: z.enum([
        "run",
        "publish",
        "pause",
        "resume",
        "duplicate",
        "archive",
        "discard_draft",
        "rollback",
        "set_visibility",
      ]),
      workflow: z
        .string()
        .optional()
        .describe("Workflow id or spoken workflow name. Omit to use the current workflow."),
      payload: z.record(z.string(), z.unknown()).optional().describe("Optional run payload for the run action."),
      targetVersion: z.number().int().positive().optional().describe("Target workflow version for rollback."),
      visibility: workflowVisibilitySchema.optional().describe("New visibility for set_visibility."),
    }),
    execute: async ({ action, workflow: workflowArg, payload, targetVersion, visibility }) => {
      const resolved = await resolveWorkflow(workflowArg);
      if (!resolved.ok) return resolved;
      const workflow = resolved.item;
      const store = useWorkflowsStore.getState();

      if (action === "publish") {
        ensurePermission(options, "canPublishWorkflow");
      }

      const actionLabel = `${action} workflow "${workflow.name}"`;
      const risk: VoiceRiskLevel =
        action === "archive" || action === "discard_draft" || action === "rollback"
          ? "destructive"
          : action === "run"
            ? "financial"
            : "write";

      return requireConfirmation(options, {
        action: `workflow_action:${action}`,
        description: actionLabel,
        details: { action, workflow: compactWorkflow(workflow), payload, targetVersion, visibility },
        risk,
        title: "Confirm workflow action",
        successMessage: `Completed: ${actionLabel}`,
        execute: async () => {
          switch (action) {
            case "run": {
              const result = await store.runWorkflow(workflow.id, payload ? { payload } : undefined);
              return { ok: true, action, workflow: compactWorkflow(workflow), runId: result.runId };
            }
            case "publish": {
              const published = await store.publishWorkflow(workflow.id);
              return { ok: true, action, workflow: compactWorkflow(published) };
            }
            case "pause": {
              await store.pauseWorkflow(workflow.id);
              return { ok: true, action, workflow: compactWorkflow(workflow) };
            }
            case "resume": {
              await store.resumeWorkflow(workflow.id);
              return { ok: true, action, workflow: compactWorkflow(workflow) };
            }
            case "duplicate": {
              const duplicate = await store.duplicateWorkflow(workflow.id);
              options.router.push(`/workflows/edit?id=${encodeURIComponent(duplicate.id)}`);
              return { ok: true, action, workflow: compactWorkflow(workflow), duplicate: compactWorkflow(duplicate) };
            }
            case "archive": {
              await store.archiveWorkflow(workflow.id);
              return { ok: true, action, workflow: compactWorkflow(workflow) };
            }
            case "discard_draft": {
              await store.discardDraft(workflow.id);
              return { ok: true, action, workflow: compactWorkflow(workflow) };
            }
            case "rollback": {
              if (!targetVersion) {
                return { ok: false, reason: "Rollback requires targetVersion." };
              }
              const rolledBack = await store.rollbackWorkflow(workflow.id, { targetVersion });
              options.router.push(`/workflows/edit?id=${encodeURIComponent(rolledBack.id)}`);
              return { ok: true, action, workflow: compactWorkflow(rolledBack) };
            }
            case "set_visibility": {
              if (!visibility) {
                return { ok: false, reason: "set_visibility requires visibility." };
              }
              await store.updateVisibility(workflow.id, visibility as WorkflowVisibility);
              return { ok: true, action, workflow: compactWorkflow(workflow), visibility };
            }
          }
        },
      });
    },
  });
}

export function createWorkflowEditorVoiceTools(options: B3OSVoiceToolsOptions) {
  return [
    defineVoiceTool({
      name: "workflow_editor_command",
      description: "Use the currently open workflow editor to save draft changes, run a test, or publish.",
      parameters: z.object({
        action: workflowEditorCommandSchema,
      }),
      execute: async ({ action }) => {
        const currentWorkflowId = getCurrentWorkflowId();
        if (!currentWorkflowId) {
          return { ok: false, reason: "No workflow editor id is present in the current URL." };
        }

        if (action === "publish") {
          ensurePermission(options, "canPublishWorkflow");
        }

        const risk: VoiceRiskLevel = action === "run_test" ? "financial" : "write";
        return requireConfirmation(options, {
          action: `workflow_editor_command:${action}`,
          description: `${action.replaceAll("_", " ")} in the current workflow editor`,
          details: { action, currentWorkflowId },
          risk,
          title: "Confirm editor action",
          successMessage: `Requested editor action: ${action.replaceAll("_", " ")}`,
          execute: () => {
            dispatchVoiceEditorCommand(action);
            return { ok: true, action, workflowId: currentWorkflowId };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "update_workflow_node_payload",
      description:
        "Patch a node title, description, or payload in the currently open workflow editor. Requires confirmation and uses the editor autosave path.",
      parameters: z.object({
        node: z.string().min(1).describe("Node id, type, title, connector, or payload key."),
        payloadPatch: z.record(z.string(), z.unknown()).optional(),
        title: z.string().optional(),
        description: z.string().optional(),
      }),
      execute: async ({ node, payloadPatch, title, description }) => {
        const currentWorkflowId = getCurrentWorkflowId();
        if (!currentWorkflowId) {
          return { ok: false, reason: "No workflow editor id is present in the current URL." };
        }
        if (!payloadPatch && title === undefined && description === undefined) {
          return { ok: false, reason: "Provide payloadPatch, title, or description to update the node." };
        }

        const workflow = await fetchWorkflowById(currentWorkflowId);
        const resolved = resolveWorkflowNode(workflow, node);
        if (!resolved.ok) return resolved;

        return requireConfirmation(options, {
          action: "update_workflow_node_payload",
          description: `Update node ${resolved.item.node.titleOverride || resolved.item.id}`,
          details: {
            workflow: compactWorkflow(workflow),
            node: compactNode(resolved.item.id, resolved.item.node),
            payloadPatch,
            title,
            description,
          },
          risk: "write",
          title: "Confirm node update",
          successMessage: "Requested node update",
          execute: () => {
            window.dispatchEvent(
              new CustomEvent(B3OS_VOICE_UPDATE_WORKFLOW_NODE_EVENT, {
                detail: {
                  nodeId: resolved.item.id,
                  dataPatch: {
                    ...(title !== undefined ? { titleOverride: title } : {}),
                    ...(description !== undefined ? { description } : {}),
                  },
                  payloadPatch,
                },
              }),
            );
            window.dispatchEvent(
              new CustomEvent(B3OS_VOICE_SELECT_WORKFLOW_NODE_EVENT, {
                detail: { nodeId: resolved.item.id },
              }),
            );
            return { ok: true, node: compactNode(resolved.item.id, resolved.item.node) };
          },
        });
      },
    }),
  ];
}

export function createPublicWidgetVoiceTools(options: B3OSVoiceToolsOptions) {
  return [
    defineVoiceTool({
      name: "public_widget_action",
      description:
        "Inspect, publish, unpublish, or open setup for a workflow public widget. Publish/unpublish requires confirmation.",
      parameters: z.object({
        action: z.enum(["get_status", "open_setup", "publish", "unpublish"]),
        workflow: z.string().optional().describe("Workflow id or spoken workflow name. Omit for current workflow."),
        title: z.string().optional(),
        description: z.string().optional(),
        buttonLabel: z.string().optional(),
        minAmount: z.string().optional(),
        maxAmount: z.string().optional(),
      }),
      execute: async ({ action, workflow: workflowArg, title, description, buttonLabel, minAmount, maxAmount }) => {
        const resolved = await resolveWorkflow(workflowArg);
        if (!resolved.ok) return resolved;
        const workflow = resolved.item;
        const widgetUrl =
          typeof window === "undefined" ? `/w/${workflow.id}` : `${window.location.origin}/w/${workflow.id}`;

        if (action === "get_status") {
          return {
            ok: true,
            workflow: compactWorkflow(workflow),
            publicWidgetEnabled: workflow.publicWidgetEnabled ?? workflow.publicWidgetConfig?.enabled ?? false,
            publicWidgetConfig: workflow.publicWidgetConfig,
            widgetUrl,
          };
        }

        if (action === "open_setup") {
          options.router.push(`/workflows/edit?id=${encodeURIComponent(workflow.id)}`);
          return {
            ok: true,
            destination: `/workflows/edit?id=${encodeURIComponent(workflow.id)}`,
            workflow: compactWorkflow(workflow),
            reason: "Opened the workflow editor. Public widget setup is available from the share controls.",
          };
        }

        ensurePermission(options, "canPublishWorkflow");

        if (action === "publish" && workflow.status === "draft" && !workflow.hasLiveVersion) {
          return { ok: false, reason: "Publish the workflow before enabling its public widget." };
        }

        const config: PublishWidgetRequest = {
          title: (title || workflow.publicWidgetConfig?.title || workflow.name).trim(),
          ...(description !== undefined ? { description: description.trim() || undefined } : {}),
          ...(buttonLabel !== undefined ? { buttonLabel: buttonLabel.trim() || undefined } : {}),
          ...(minAmount !== undefined ? { minAmount: minAmount.trim() || undefined } : {}),
          ...(maxAmount !== undefined ? { maxAmount: maxAmount.trim() || undefined } : {}),
        };

        return requireConfirmation(options, {
          action: `public_widget_action:${action}`,
          description:
            action === "publish"
              ? `Enable public widget for "${workflow.name}"`
              : `Disable public widget for "${workflow.name}"`,
          details: { action, workflow: compactWorkflow(workflow), config, widgetUrl },
          risk: "write",
          title: "Confirm public widget action",
          successMessage: action === "publish" ? "Public widget enabled" : "Public widget disabled",
          execute: async () => {
            const store = useWorkflowsStore.getState();
            if (action === "publish") {
              const widget = await store.publishWidgetConfig(workflow.id, config);
              return { ok: true, action, workflow: compactWorkflow(workflow), widget, widgetUrl };
            }
            await store.unpublishWidgetConfig(workflow.id);
            return { ok: true, action, workflow: compactWorkflow(workflow), widgetUrl };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "ask_caddie_to_edit_workflow",
      description: "Send an editor-focused prompt to Caddie for the currently open workflow.",
      parameters: z.object({
        prompt: z.string().min(1),
      }),
      execute: async ({ prompt }) => {
        const currentWorkflowId = getCurrentWorkflowId();
        if (!currentWorkflowId) {
          return { ok: false, reason: "No workflow editor id is present in the current URL." };
        }
        useAIChatStore.getState().setPendingMessage(prompt.trim());
        useAIChatStore.getState().setPendingErrorBlock(null);
        useAIChatStore.getState().openPanel();
        return { ok: true, prompt, currentWorkflowId };
      },
    }),
    defineVoiceTool({
      name: "submit_current_editor_prompt",
      description: "Submit a natural-language workflow edit request to Caddie from anywhere in the editor.",
      parameters: z.object({
        prompt: z.string().min(1),
      }),
      execute: async ({ prompt }) => {
        const currentWorkflowId = getCurrentWorkflowId();
        if (!currentWorkflowId) {
          return { ok: false, reason: "No workflow editor id is present in the current URL." };
        }
        submitPendingPrompt(prompt.trim(), options.router);
        return { ok: true, prompt, currentWorkflowId };
      },
    }),
  ];
}
